// The waves screen's data and writes. Query keys start with the entity kind ('wave'), so a change event of that kind refetches
// (lib/events.ts). A URL carries ids only: `?wave=` is a wave id (wv_), `?list=` a shared list id (ls_).
import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query'
import type { z } from 'zod'
import type { Checklist, Wave } from '@nw/shared/api/lists'
import { api, apiPath } from '../../lib/api.ts'
import type { NameChoice } from '../touches/names.ts'

export type { Wave }
export type ChecklistData = z.infer<typeof Checklist>

const ID = '[0-9a-hjkmnp-tv-z]{26}'
const WAVE_ID = new RegExp(`^wv_${ID}$`)
const LIST_ID = new RegExp(`^ls_${ID}$`)

/** Router validateSearch: the selected wave and the list a new wave starts from, if they are ids (ids only in a URL). */
export function parseSearch(search: Record<string, unknown>): { wave?: string; list?: string } {
  const wave = typeof search.wave === 'string' && WAVE_ID.test(search.wave) ? search.wave : undefined
  const list = typeof search.list === 'string' && LIST_ID.test(search.list) ? search.list : undefined
  return { ...(wave ? { wave } : {}), ...(list ? { list } : {}) }
}

export const wavesQuery = infiniteQueryOptions({
  queryKey: ['wave', 'page'] as const,
  initialPageParam: undefined as string | undefined,
  queryFn: ({ pageParam, signal }) => api.get<{ items: Wave[]; next: string | null }>(apiPath('/api/waves', {}, { after: pageParam, limit: 100 }), signal),
  getNextPageParam: (last) => last.next ?? undefined,
})

export const checklistQuery = (waveId: string) =>
  queryOptions({
    queryKey: ['wave', waveId, 'checklist'] as const,
    queryFn: ({ signal }) => api.get<ChecklistData>(apiPath('/api/waves/:waveId/checklist', { waveId }), signal),
  })

export type Family = 'tusen' | 'betong' | 'sauda' | 'tommy'

export interface NewWave {
  listId: string
  name: string
  family: Family
  plannedDate?: string
  templateId?: string
  templateText?: string
}
export const createWave = (body: NewWave) => api.post<Wave>('/api/waves', body)

export interface WaveChange {
  version: number
  plannedDate?: string | null
  templateId?: string | null
  templateText?: string
  status?: Wave['status']
  /** after a 409 names_in_text (CONTRACTS.md section 4) */
  names?: NameChoice
}
export const patchWave = (id: string, body: WaveChange) => api.patch<Wave>(apiPath('/api/waves/:waveId', { waveId: id }), body)
